// src/components/Layout.js - DUAL SYSTEM ARCHITECTURE WITH EXPLICIT IDS & ICONS + I18N + SEARCH
// CORRIGIDO: Substituído isITUser por userPermissions

import React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Layout.module.css';
import useAuth from '../../../hooks/useAuth';
import { db } from '../../../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import NotificationBell from '../Notification/NotificationBell';
import { useNotifications } from '../../../contexts/NotificationContext';
import SettingsModal from '../Settings/SettingsModal';
import { FaComments, FaSearch } from 'react-icons/fa';
import Chat from '../Chat/Chat';

// Import React Icons
import {
    FaReceipt, FaIndustry, FaMoneyCheck, FaChartBar, FaHeadset, FaUsers, FaLock, FaClipboardCheck, FaFileInvoice, FaClipboardList, FaSignOutAlt, FaCog
} from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { MdSpaceDashboard, MdLibraryBooks } from 'react-icons/md';
import { BsBeakerFill } from 'react-icons/bs'
import { LuBoxes } from 'react-icons/lu'
import { VscNotebookTemplate } from "react-icons/vsc";
import { GrDocumentConfig } from "react-icons/gr"

// ============================================
// STEP 1: ROLE MATRIX CONFIGURATIONS
// ============================================
const ROLE_BRACKETS = {
    BASE_ONLY: ['Laboratorio', 'Logistica', 'Marketing', 'Comercial', 'Operacional'],
    LUXURY_ONLY: ['Inspetor', 'Visitante'],
    DUAL_ACCESS: ['TI', 'Diretoria (Admin)']
};

// ============================================
// STEP 2: SPLIT NAVIGATION ARRAYS WITH EXPLICIT IDs & ICONS
// ============================================

// Icon mapping for menu items
const ICON_MAP = {
    // Base ERP Icons
    dashboard: <MdSpaceDashboard />,
    parametros: <GrDocumentConfig />,
    cadastros: <MdLibraryBooks />,
    projeto: <BsBeakerFill />,
    pedidos: <FaReceipt />,
    producao: <FaIndustry />,
    financeiro: <FaMoneyCheck />,
    fiscal: <FaFileInvoice />,
    'controle-de-qualidade': <FaClipboardCheck />,
    estoque: <LuBoxes />,
    relatorios: <FaChartBar />,
    helpdesk: <FaHeadset />,
    usuarios: <FaUsers />,
    permissoes: <FaLock />,

    // TUV Icons
    tuv_dashboard: <MdSpaceDashboard />,
    tuv_cadastros: <MdLibraryBooks />,
    tuv_models: <VscNotebookTemplate />,
    tuv_chekclist: <FaClipboardList />,
    tuv_relatorios: <FaChartBar />,
    tuv_helpdesk: <FaHeadset />,
    tuv_permissoes: <FaLock />,
};

// Helper to get icon for item
const getIconForItem = (item) => {
    if (item.id && ICON_MAP[item.id]) {
        return ICON_MAP[item.id];
    }
    // Try to find by text match
    const textKey = item.text.toLowerCase().replace(/\s+/g, '_');
    if (ICON_MAP[textKey]) return ICON_MAP[textKey];
    return ICON_MAP.default;
};

// ============================================
// IMPORTANT: Generate ID from ORIGINAL Portuguese text
// This ensures the ID matches what's in the database
// ============================================
const generateIdFromText = (text) => {
    return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, '_')
        .replace(/[^\w]/g, '');
};

// Menu item translation keys mapping
const getTranslationKey = (text) => {
    const keyMap = {
        'Dashboard': 'navigation.dashboard',
        'Parâmetros': 'navigation.parameters',
        'Cadastros': 'navigation.registrations',
        'Projeto': 'navigation.projects',
        'Pedidos': 'navigation.orders',
        'Produção': 'navigation.production',
        'Financeiro': 'navigation.financial',
        'Fiscal': 'navigation.fiscal',
        'Controle de Qualidade': 'navigation.quality',
        'Estoque': 'navigation.inventory',
        'Relatórios': 'navigation.reports',
        'AjudaAí!': 'navigation.helpdesk',
        'Usuários': 'navigation.users',
        'Permissões': 'navigation.permissions',
        'Checklist': 'navigation.checklist',
        'Relatório': 'navigation.reports',
        'Modelos': 'navigation.models',
        // Sub-module translations
        'Cargos': 'navigation.positions',
        'Prefixos': 'navigation.prefixes',
        'Inventário': 'navigation.inventory',
        'Região': 'navigation.region',
        'Unidade de Medida': 'navigation.unitOfMeasure',
        'Situação de Cheque': 'navigation.checkStatus',
        'Público Alvo': 'navigation.targetAudience',
        'Modelos de Projeto': 'navigation.projectModels',
        'Cores': 'navigation.colors',
        'Critério de Qualidade': 'navigation.qualityCriteria',
        'Parâmetros de Preparo': 'navigation.preparationSetup',
        'Embalagem': 'navigation.packaging',
        'Motivo de Contato (SAC)': 'navigation.contactReason',
        'Situação (SAC)': 'navigation.situation',
        'Motivo Devolução Produto': 'navigation.productReturn',
        'Motivo Alteração Estoque': 'navigation.stockChange',
        'Categoria de Produtos': 'navigation.productCategory',
        'Sub-Categoria': 'navigation.subCategory',
        'Bancos': 'navigation.banks',
        'Condição de Faturamento': 'navigation.billingCondition',
        'Tipos': 'navigation.types',
        'Tipo de Cliente': 'navigation.clientType',
        'Tipo de Caixa': 'navigation.boxType',
        'Tipo de Produto': 'navigation.productType',
        'Tipo de Endereço': 'navigation.addressType',
        'Tipo de Telefone': 'navigation.phoneType',
        'Tipo de Contato': 'navigation.contactType',
        'Tipo Resultado Contato': 'navigation.contactResultType',
        'Tipo Doc. Pagamento': 'navigation.paymentDocType',
        'Tipo de Projeto': 'navigation.projectType',
        'Tipo de Função': 'navigation.positionType',
        'Tipo de Natureza': 'navigation.natureType',
        'Tipo de Empresa': 'navigation.companyType',
        'Matéria-Prima': 'navigation.rawMaterials',
        'Produtos': 'navigation.products',
        'Fornecedores': 'navigation.suppliers',
        'Procedimentos': 'navigation.procedures',
        'Clientes': 'navigation.clients',
        'Executivos de Conta': 'navigation.accountExecutives',
        'Transportadoras': 'navigation.carriers',
        'Desenvolvimento': 'navigation.development',
        'Acompanhamento de Projeto': 'navigation.projectTracking',
        'Pedido de Venda': 'navigation.salesOrder',
        'Pedido de Compra': 'navigation.purchaseOrder',
        'Requisição de Compra': 'navigation.purchaseRequest',
        'Aprovação de Pedido de Venda': 'navigation.salesOrderApproval',
        'Aprovação de Pedido de Compra': 'navigation.purchaseOrderApproval',
        'Entrada do Pedido de Compra': 'navigation.purchaseOrderEntry',
        'Emissão de Etiqueta de Código de Barras': 'navigation.barcodeEmission',
        'Ordens de Produção': 'navigation.productionOrders',
        'Fases de Produção': 'navigation.productionPhases',
        'Forecast': 'navigation.forecast',
        'Centro de Custo': 'navigation.costCenter',
        'Plano de Contas': 'navigation.accountPlan',
        'Manifesto do Destinatário': 'navigation.recipientManifest',
        'Cadastro de NCM': 'navigation.ncmRegister',
        'Cadastro de CFOP': 'navigation.cfopRegister',
        'Faturamento (Emissão de NF)': 'navigation.invoiceIssuing',
        'Análise de Embalagens': 'navigation.packagingAnalysis',
        'Localizações': 'navigation.locations',
        'Entrada de Estoque': 'navigation.stockEntry',
        'Saída de Estoque': 'navigation.stockOutflow',
        'Consulta de Estoque': 'navigation.stockQuery',
        'Estoque Atual': 'navigation.currentStock',
        'Listagem de Amostras': 'navigation.sampleList',
        'Relatório de Usuários': 'navigation.userReport',
        'Listagem de Clientes': 'navigation.clientList',
        'Listagem de Fornecedores': 'navigation.supplierList',
        'Listagem de Transportadoras': 'navigation.carrierList',
        'Listagem de Produtos': 'navigation.productList',
        'Ficha de Produto': 'navigation.productRecord',
        'Central de Suporte': 'navigation.supportCenter',
        'Gerenciamento de Tickets': 'navigation.ticketManagement',
        'Checklists': 'navigation.checklists',
        'Balanço de Moeda': 'navigation.balance',
    };
    return keyMap[text] || text;
};

// ============================================
// CRITICAL FIX: Get item ID from ORIGINAL text
// ALWAYS use the original Portuguese text from the menu definition
// ============================================
const getItemId = (item) => {
    // If the item has an explicit id, use it
    if (item.id) return item.id;

    // IMPORTANT: Generate ID from the ORIGINAL Portuguese text
    // The item.text is the original text from the menu definition
    // Even after translation, the item object still has the original text
    // We need to use the ORIGINAL text, not the translated text
    return generateIdFromText(item.text);
};

// Helper function to translate menu items for display only
// Preserves original text for permission checking
const translateMenuItem = (item, t) => {
    if (!item) return item;

    const translated = { ...item };
    // Store the original text before translation
    translated.originalText = item.text;
    const translationKey = getTranslationKey(item.text);
    translated.text = t(translationKey);

    if (item.subModules) {
        translated.subModules = item.subModules.map(subItem => {
            if (subItem.type === 'sub-group') {
                return {
                    ...subItem,
                    originalText: subItem.text,
                    text: t(getTranslationKey(subItem.text)),
                    subModules: subItem.subModules.map(link => ({
                        ...link,
                        originalText: link.text,
                        text: t(getTranslationKey(link.text))
                    }))
                };
            }
            return {
                ...subItem,
                originalText: subItem.text,
                text: t(getTranslationKey(subItem.text))
            };
        });
    }

    return translated;
};

// BASE ERP MENU - Industrial & Manufacturing Focus
const menuItemsBase = [
    { id: 'dashboard', text: 'Dashboard', path: '/dashboard', type: 'link' },
    {
        id: 'parametros',
        text: 'Parâmetros', type: 'group',
        subModules: [
            { text: 'Cargos', path: '/parametros/cargos' },
            { text: 'Prefixos', path: '/parametros/prefixos' },
            { text: 'Modelos', path: '/parametros/modelos' },
            { text: 'Inventário', path: '/parametros/inventario' },
            { text: 'Região', path: '/parametros/regiao' },
            { text: 'Unidade de Medida', path: '/parametros/unidade-de-medida' },
            { text: 'Situação de Cheque', path: '/parametros/situacao-de-cheque' },
            { text: 'Público Alvo', path: '/parametros/publico-alvo' },
            { text: 'Modelos de Projeto', path: '/parametros/modelos-de-projeto' },
            { text: 'Cores', path: '/parametros/cores' },
            { text: 'Critério de Qualidade', path: '/parametros/criterio-de-qualidade' },
            { text: 'Parâmetros de Preparo', path: '/parametros/setup-de-preparo' },
            { text: 'Embalagem', path: '/parametros/embalagem' },
            { text: 'Motivo de Contato (SAC)', path: '/parametros/motivo-de-contato' },
            { text: 'Situação (SAC)', path: '/parametros/situacao' },
            { text: 'Motivo Devolução Produto', path: '/parametros/motivo-de-devolucao-produto' },
            { text: 'Motivo Alteração Estoque', path: '/parametros/motivo-de-alteracao-de-estoque' },
            { text: 'Categoria de Produtos', path: '/parametros/categoria-de-produto' },
            { text: 'Sub-Categoria', path: '/parametros/sub-categoria' },
            { text: 'Bancos', path: '/parametros/bancos' },
            { text: 'Condição de Faturamento', path: '/parametros/condicao-de-faturamento' },
            {
                id: 'tipos',
                text: 'Tipos', path: '/parametros/tipos', type: 'sub-group',
                subModules: [
                    { text: 'Tipo de Cliente', path: '/parametros/tipo/cliente' },
                    { text: 'Tipo de Caixa', path: '/parametros/tipo/caixa' },
                    { text: 'Tipo de Palete', path: '/parametros/tipo/palete' },
                    { text: 'Tipo de Produto', path: '/parametros/tipo/produto' },
                    { text: 'Tipo de Endereço', path: '/parametros/tipo/endereco' },
                    { text: 'Tipo de Telefone', path: '/parametros/tipo/telefone' },
                    { text: 'Tipo de Contato', path: '/parametros/tipo/contato' },
                    { text: 'Tipo Resultado Contato', path: '/parametros/tipo/resultado-contato' },
                    { text: 'Tipo Doc. Pagamento', path: '/parametros/tipo/documento-pagamento' },
                    { text: 'Tipo de Projeto', path: '/parametros/tipo/projeto' },
                    { text: 'Tipo de Função', path: '/parametros/tipo/funcao' },
                    { text: 'Tipo de Natureza', path: '/parametros/tipo/natureza' },
                    { text: 'Tipo de Empresa', path: '/parametros/tipo/empresa' },
                ]
            },
        ]
    },
    {
        id: 'cadastros',
        text: 'Cadastros', type: 'group',
        subModules: [
            { text: 'Matéria-Prima', path: '/cadastros/materia-prima' },
            { text: 'Produtos', path: '/cadastros/produtos' },
            { text: 'Fornecedores', path: '/cadastros/fornecedores' },
            { text: 'Procedimentos', path: '/cadastros/procedimentos' },
            { text: 'Clientes', path: '/cadastros/clientes' },
            { text: 'Executivos de Conta', path: '/cadastros/executivos-de-conta' },
            { text: 'Transportadoras', path: '/cadastros/transportadora' },
        ]
    },
    {
        id: 'projeto',
        text: 'Projeto', type: 'group',
        subModules: [
            { text: 'Desenvolvimento', path: '/projeto/desenvolvimento' },
            { text: 'Acompanhamento de Projeto', path: '/projeto/acompanhamento-de-projeto' }
        ]
    },
    {
        id: 'pedidos',
        text: 'Pedidos', type: 'group',
        subModules: [
            { text: 'Pedido de Venda', path: '/pedidos/pedido-de-venda' },
            { text: 'Pedido de Compra', path: '/pedidos/pedido-de-compra' },
            { text: 'Requisição de Compra', path: '/pedidos/requisicao-de-compra' },
            { text: 'Retorno de pedido para Análise', path: '/pedidos/retorno-para-analise'},
            { text: 'Aprovação de Pedido de Venda', path: '/pedidos/aprovacao-de-pedido-de-venda' },
            { text: 'Aprovação de Pedido de Compra', path: '/pedidos/aprovacao-de-pedido-de-compra' },
            { text: 'Entrada do Pedido de Compra', path: '/pedidos/entrada-de-pedido-de-compra' },
        ]
    },
    {
        id: 'producao',
        text: 'Produção', type: 'group',
        subModules: [
            { text: 'Emissão de Etiqueta de Código de Barras', path: '/producao/emissao-de-codigo-de-barras' },
            { text: 'Ordens de Produção', path: '/producao/ordens-de-producao' },
            { text: 'Fases de Produção', path: '/producao/fases-de-producao' },
            { text: 'Forecast', path: '/producao/forecast' },
        ]
    },
    {
        id: 'financeiro',
        text: 'Financeiro', type: 'group',
        subModules: [
            { text: 'Centro de Custo', path: '/financeiro/centro-de-custo' },
            { text: 'Plano de Contas', path: '/financeiro/plano-de-contas' },
        ]
    },
    {
        id: 'fiscal',
        text: 'Fiscal', type: 'group',
        subModules: [
            { text: 'Manifesto do Destinatário', path: '/fiscal/manifesto-do-destinatario' },
            { text: 'Cadastro de NCM', path: '/fiscal/cadastro-de-ncm' },
            { text: 'Cadastro de CFOP', path: '/fiscal/cadastro-de-cfop' },
            { text: 'Faturamento (Emissão de NF)', path: '/fiscal/emissao-de-nfe'}
        ]
    },
    {
        id: 'controle-de-qualidade',
        text: 'Controle de Qualidade', type: 'group',
        subModules: [
            { text: 'Análise de Embalagens', path: '/controle-de-qualidade/analise-de-embalagens' }
        ]
    },
    {
        id: 'estoque',
        text: 'Estoque', type: 'group',
        subModules: [
            { text: 'Localizações', path: '/estoque/embalagens/localizacoes' },
            { text: 'Entrada de Estoque', path: '/estoque/embalagens/entrada-de-estoque' },
            { text: 'Saída de Estoque', path: '/estoque/embalagens/saida-de-estoque' },
            { text: 'Consulta de Estoque', path: '/estoque/embalagens/consulta-de-estoque' },
        ]
    },
    {
        id: 'relatorios',
        text: 'Relatórios', path: '/relatorios', type: 'group',
        subModules: [
            { text: 'Estoque Atual', path: '/relatorios/estoque-atual' },
            { text: 'Listagem de Amostras', path: '/relatorios/amostras' },
            { text: 'Relatório de Usuários', path: '/relatorios/usuarios' },
            { text: 'Listagem de Clientes', path: '/relatorios/clientes' },
            { text: 'Listagem de Fornecedores', path: '/relatorios/fornecedores' },
            { text: 'Listagem de Transportadoras', path: '/relatorios/transportadoras' },
            { text: 'Listagem de Produtos', path: '/relatorios/produtos' },
            { text: 'Ficha de Produto', path: '/relatorios/ficha-de-produto' },
            { text: 'Relatório de Suporte', path: '/relatorios/suporte' },
        ]
    },
    {
        id: 'helpdesk',
        text: 'AjudaAí!', path: '/helpdesk', type: 'group',
        subModules: [
            { text: 'Central de Suporte', path: '/helpdesk/central-de-suporte' },
            { id: 'tickets_manager', text: 'Gerenciamento de Tickets', path: '/helpdesk/gerenciamento-de-tickets' },
        ]
    },
    { id: 'usuarios', text: 'Usuários', path: '/usuarios', type: 'link' },
    { id: 'permissoes', text: 'Permissões', path: '/permissoes', type: 'link' },
];

// TUV ERP MENU - VIP & Concierge Focus
const menuItemsTUV = [
    { id: 'tuv_dashboard', text: 'Dashboard', path: '/tuv/dashboard', type: 'link' },
    {
        id: 'tuv_cadastros',
        text: 'Cadastros', type: 'group',
        subModules: [
        ]
    },
    {
        id: 'tuv_models',
        text: 'Modelos', type: 'group',
        subModules: [
            { id: 'tuv_modelo_chekclist', text: 'Checklists', path: '/tuv/modelos/checklists' },
        ]
    },
    { id: 'tuv_chekclist', text: 'Checklist', path: '/tuv/checklist', type: 'link' },
    { id: 'tuv_relatorios', text: 'Relatório', path: '/tuv/relatorio', type: 'link' },
    {
        id: 'helpdesk',
        text: 'AjudaAí!', path: '/helpdesk', type: 'group',
        subModules: [
            { text: 'Central de Suporte', path: '/helpdesk/central-de-suporte' },
            { id: 'tickets_manager', text: 'Gerenciamento de Tickets', path: '/helpdesk/gerenciamento-de-tickets' },
        ]
    },
    { id: 'tuv_permissoes', text: 'Permissões', path: '/tuv/permissoes' },
];

const SYSTEM_TYPES = {
    BASE: 'base',
    LUXURY: 'luxury'
};

const STORAGE_KEY = 'erp_system_preference';

// Helper to determine user's role bracket
const getUserRoleBracket = (role) => {
    if (!role) return null;
    if (ROLE_BRACKETS.BASE_ONLY.includes(role)) return 'BASE_ONLY';
    if (ROLE_BRACKETS.LUXURY_ONLY.includes(role)) return 'LUXURY_ONLY';
    if (ROLE_BRACKETS.DUAL_ACCESS.includes(role)) return 'DUAL_ACCESS';
    return null;
};

const Layout = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser, loading: authLoading, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // ============================================
    // CORREÇÃO: Usar userPermissions em vez de isITUser
    // ============================================
    const {
        userPermissions,
        isLoading: notifLoading
    } = useNotifications();

    const [isChatOpen, setIsChatOpen] = useState(false);
    const [selectedChatChannelId, setSelectedChatChannelId] = useState(null);

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isGroupExpanded, setIsGroupExpanded] = useState({});
    const [isSubGroupExpanded, setIsSubGroupExpanded] = useState({});
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    const [activeSystem, setActiveSystem] = useState(SYSTEM_TYPES.BASE);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const [dbPermissions, setDbPermissions] = useState({});
    const [permsLoading, setPermsLoading] = useState(true);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    const userRole = currentUser?.role;

    // ============================================
    // CORREÇÃO: Verificar se usuário tem permissões de notificação
    // ============================================
    const hasNotificationPermissions = userPermissions && userPermissions.length > 0;

    // ============================================
    // HANDLE CHAT NOTIFICATION CLICKS - FROM BELL
    // ============================================
    const handleChatNotificationClick = useCallback((notification) => {
        // Check if it's a chat notification
        if (notification.module === 'chat' || notification.isChatNotification || notification.actionType === 'open_chat') {
            // Get the channel ID from notification
            const channelId = notification.channelId || notification.data?.channelId;

            if (channelId) {
                setSelectedChatChannelId(channelId);
            } else {
                setSelectedChatChannelId(null);
            }

            // Open the chat overlay
            setIsChatOpen(true);
        }
    }, []);

    // ============================================
    // GLOBAL EVENT LISTENER FOR CHAT NOTIFICATIONS
    // Also listen for the custom event from NotificationBell
    // ============================================
    useEffect(() => {
        const handleOpenChatEvent = (event) => {
            const { channelId } = event.detail || {};
            if (channelId) {
                setSelectedChatChannelId(channelId);
            } else {
                setSelectedChatChannelId(null);
            }
            setIsChatOpen(true);
        };

        // Listen for the custom event
        window.addEventListener('openChat', handleOpenChatEvent);

        // Also listen for the notification click event that might be dispatched from NotificationBell
        const handleNotificationClick = (event) => {
            const { channelId } = event.detail || {};
            if (channelId) {
                setSelectedChatChannelId(channelId);
                setIsChatOpen(true);
            }
        };
        window.addEventListener('chatNotificationClick', handleNotificationClick);

        return () => {
            window.removeEventListener('openChat', handleOpenChatEvent);
            window.removeEventListener('chatNotificationClick', handleNotificationClick);
        };
    }, []);

    // When closing chat, clear the selected channel ID
    const handleCloseChat = useCallback(() => {
        setIsChatOpen(false);
        // Clear the selected channel ID after a delay
        // so it doesn't affect re-opening
        setTimeout(() => {
            setSelectedChatChannelId(null);
        }, 300);
    }, []);

    // Determine active system based on user role
    useEffect(() => {
        if (!userRole || authLoading) return;

        const roleBracket = getUserRoleBracket(userRole);

        if (roleBracket === 'BASE_ONLY') {
            setActiveSystem(SYSTEM_TYPES.BASE);
            localStorage.setItem(STORAGE_KEY, SYSTEM_TYPES.BASE);
        }
        else if (roleBracket === 'LUXURY_ONLY') {
            setActiveSystem(SYSTEM_TYPES.LUXURY);
            localStorage.setItem(STORAGE_KEY, SYSTEM_TYPES.LUXURY);
        }
        else if (roleBracket === 'DUAL_ACCESS') {
            const savedPreference = localStorage.getItem(STORAGE_KEY);
            if (savedPreference && Object.values(SYSTEM_TYPES).includes(savedPreference)) {
                setActiveSystem(savedPreference);
            } else {
                setActiveSystem(SYSTEM_TYPES.BASE);
                localStorage.setItem(STORAGE_KEY, SYSTEM_TYPES.BASE);
            }
        }
    }, [userRole, authLoading]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keyboard shortcut for search (Ctrl+K or Cmd+K)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
            if (e.key === 'Escape') {
                setSearchQuery('');
                searchInputRef.current?.blur();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Fetch permissions from Firestore
    useEffect(() => {
        const fetchPermissions = async () => {
            if (!userRole) {
                setPermsLoading(false);
                return;
            }

            // Check if user is Admin or IT - they get full access, skip DB fetch
            if (userRole === 'Diretoria (Admin)' || userRole === 'TI') {
                setDbPermissions({});
                setPermsLoading(false);
                return;
            }

            try {
                const docId = `${userRole}_${activeSystem}`;
                const docRef = doc(db, 'module_permissions', docId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setDbPermissions(docSnap.data());
                } else {
                    setDbPermissions({});
                }
            } catch (error) {
                console.error('Error fetching permissions:', error);
                setDbPermissions({});
            } finally {
                setPermsLoading(false);
            }
        };

        if (!authLoading) fetchPermissions();
    }, [userRole, authLoading, activeSystem]);

    // Get current menu items based on active system and translate them
    const baseMenuItems = activeSystem === SYSTEM_TYPES.BASE ? menuItemsBase : menuItemsTUV;
    const currentMenuItems = baseMenuItems.map(item => translateMenuItem(item, t));

    // ============================================
    // CRITICAL FIX: Check access using the ORIGINAL ID
    // The ID must be generated from the ORIGINAL Portuguese text
    // NOT from the translated text
    // ============================================
    const hasAccess = (item) => {
        if (permsLoading) return false;

        // CRITICAL FIX: Use originalText to get the ID, NOT the translated text
        // The originalText is preserved during translation
        const textToUse = item.originalText || item.text;
        const itemId = getItemId({ ...item, text: textToUse });

        if (activeSystem === SYSTEM_TYPES.LUXURY) {
            if (Object.keys(dbPermissions).length === 0 && permsLoading === false) {
                if (userRole === 'Diretoria (Admin)' || userRole === 'TI') {
                    return true;
                }
                return false;
            }
            return dbPermissions[itemId] === true;
        }

        if (Object.keys(dbPermissions).length === 0 && permsLoading === false) {
            if (userRole === 'Diretoria (Admin)' || userRole === 'TI') {
                return true;
            }
            return false;
        }

        return dbPermissions[itemId] === true;
    };

    // ============================================
    // SEARCH FILTERING LOGIC - Only show matching items
    // ============================================
    const getSearchableText = (item) => {
        const text = (item.originalText || item.text || '').toLowerCase();
        const translatedText = (item.text || '').toLowerCase();
        return { original: text, translated: translatedText };
    };

    const matchesSearch = (item, query) => {
        if (!query.trim()) return true;
        const searchTerm = query.toLowerCase().trim();
        const searchable = getSearchableText(item);

        // Check if the item itself matches
        if (searchable.original.includes(searchTerm) || searchable.translated.includes(searchTerm)) {
            return true;
        }
        return false;
    };

    // Filter menu items - only show items that match the search
    const getFilteredMenuItems = () => {
        if (!searchQuery.trim()) return currentMenuItems;

        const filtered = [];

        currentMenuItems.forEach(item => {
            // For link type items
            if (item.type === 'link') {
                if (matchesSearch(item, searchQuery) && hasAccess(item)) {
                    filtered.push(item);
                }
                return;
            }

            // For group type items
            if (item.type === 'group') {
                // Check if the group itself matches
                const groupMatches = matchesSearch(item, searchQuery);

                // Filter sub-modules that match
                const matchingSubModules = [];

                item.subModules.forEach(subItem => {
                    // For sub-group items
                    if (subItem.type === 'sub-group') {
                        const subGroupMatches = matchesSearch(subItem, searchQuery);
                        const matchingLinks = subItem.subModules.filter(link => {
                            return matchesSearch(link, searchQuery) && hasAccess(link);
                        });

                        // Include sub-group if it matches OR if it has matching links
                        if (subGroupMatches || matchingLinks.length > 0) {
                            const filteredSubGroup = {
                                ...subItem,
                                subModules: subGroupMatches ?
                                    subItem.subModules.filter(link => hasAccess(link)) :
                                    matchingLinks
                            };
                            matchingSubModules.push(filteredSubGroup);
                        }
                        return;
                    }

                    // For regular sub-items
                    if (matchesSearch(subItem, searchQuery) && hasAccess(subItem)) {
                        matchingSubModules.push(subItem);
                    }
                });

                // Include group if it matches OR if it has matching sub-modules
                if (groupMatches || matchingSubModules.length > 0) {
                    // If group matches, include all accessible sub-modules
                    // Otherwise, only include the matching ones
                    const filteredItem = {
                        ...item,
                        subModules: groupMatches ?
                            item.subModules.filter(sub => hasAccess(sub)) :
                            matchingSubModules
                    };
                    filtered.push(filteredItem);
                }
            }
        });

        return filtered;
    };

    const filteredMenuItems = getFilteredMenuItems();

    // Check if there are any results
    const hasSearchResults = filteredMenuItems.some(item => {
        if (item.type === 'link') return true;
        if (item.subModules) {
            return item.subModules.some(subItem => {
                if (subItem.type === 'sub-group') {
                    return subItem.subModules.some(link => hasAccess(link));
                }
                return hasAccess(subItem);
            });
        }
        return false;
    });

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isLinkActive = (path) => location.pathname === path;

    const handleSystemSwitch = (targetSystem) => {
        if (targetSystem === activeSystem) {
            setIsDropdownOpen(false);
            return;
        }

        setActiveSystem(targetSystem);
        localStorage.setItem(STORAGE_KEY, targetSystem);
        setIsGroupExpanded({});
        setIsSubGroupExpanded({});
        setIsDropdownOpen(false);
        setSearchQuery(''); // Clear search on system switch

        const targetPath = targetSystem === SYSTEM_TYPES.BASE ? '/dashboard' : '/tuv/dashboard';
        navigate(targetPath);
    };

    // Handle group click - closes other groups
    const handleGroupClick = (groupKey) => {
        if (!isSidebarOpen) {
            // If sidebar is closed, open it first
            setIsSidebarOpen(true);
            // Then expand the group after a small delay
            setTimeout(() => {
                setIsGroupExpanded(prev => {
                    const newState = {};
                    Object.keys(prev).forEach(key => {
                        newState[key] = false;
                    });
                    newState[groupKey] = true;
                    return newState;
                });
            }, 300);
            return;
        }

        setIsGroupExpanded(prev => {
            const newState = {};
            Object.keys(prev).forEach(key => {
                newState[key] = false;
            });
            newState[groupKey] = !prev[groupKey];
            return newState;
        });
    };

    // Handle subgroup click
    const handleSubGroupClick = (subGroupKey, parentGroupKey) => {
        setIsSubGroupExpanded(prev => {
            const newState = {};
            Object.keys(prev).forEach(key => {
                if (key.startsWith(parentGroupKey)) {
                    newState[key] = false;
                } else {
                    newState[key] = prev[key];
                }
            });
            newState[subGroupKey] = !prev[subGroupKey];
            return newState;
        });
    };

    // Handle link click - if sidebar is closed, open it
    const handleLinkClick = () => {
        if (!isSidebarOpen) {
            setIsSidebarOpen(true);
        }
        // Clear search on navigation
        setSearchQuery('');
    };

    const renderLinkContent = (text, path, level, icon) => (
        <Link
            to={path}
            className={`${styles.navLink} ${level === 2 ? styles.navLinkLevel2 : ''} ${level === 3 ? styles.navLinkLevel3 : ''} ${isLinkActive(path) ? styles.navItemActive : ''}`}
            onClick={handleLinkClick}
            title={!isSidebarOpen ? text : ''}
        >
            {icon && <span className={styles.navIcon}>{icon}</span>}
            <span className={styles.navText}>{text}</span>
        </Link>
    );

    const renderSubGroupItem = (subItem, parentGroupKey, forceExpanded = false) => {
        if (!hasAccess(subItem)) return null;

        const accessibleLinks = subItem.subModules.filter(link => hasAccess(link));

        if (accessibleLinks.length === 0) return null;

        // Use original text for the subgroup key to ensure consistency
        const subGroupKey = `${parentGroupKey}_${(subItem.originalText || subItem.text).replace(/\s/g, '')}`;
        const isSubGroupActive = accessibleLinks.some(link => isLinkActive(link.path));
        const isExpanded = forceExpanded || isSubGroupExpanded[subGroupKey] || isSubGroupActive;
        const icon = getIconForItem(subItem);

        return (
            <li key={subItem.originalText || subItem.text} className={styles.navSubGroup}>
                <div
                    className={`${styles.navLink} ${styles.navLinkLevel2} ${styles.navSubGroupHeader}`}
                    onClick={() => handleSubGroupClick(subGroupKey, parentGroupKey)}
                    title={!isSidebarOpen ? subItem.text : ''}
                >
                    {icon && <span className={styles.navIcon}>{icon}</span>}
                    <span className={styles.navText}>{subItem.text}</span>
                    {isSidebarOpen && (
                        <span className={styles.navGroupIcon}>{isExpanded ? '▲' : '▼'}</span>
                    )}
                </div>
                {isSidebarOpen && (
                    <ul className={`${styles.navSubListLevel3} ${isExpanded ? styles.isExpanded : styles.isCollapsed}`}>
                        {accessibleLinks.map(link => (
                            <li key={link.path} className={styles.navSubItemLevel3}>
                                {renderLinkContent(link.text, link.path, 3, null)}
                            </li>
                        ))}
                    </ul>
                )}
            </li>
        );
    };

    const renderModuleItem = (item, forceExpanded = false) => {
        if (!hasAccess(item)) return null;

        const icon = getIconForItem(item);

        if (item.type === 'link') {
            return (
                <li key={item.path} className={styles.navItem}>
                    <Link
                        to={item.path}
                        className={`${styles.navLink} ${isLinkActive(item.path) ? styles.navItemActive : ''}`}
                        onClick={handleLinkClick}
                        title={!isSidebarOpen ? item.text : ''}
                    >
                        {icon && <span className={styles.navIcon}>{icon}</span>}
                        <span className={styles.navText}>{item.text}</span>
                    </Link>
                </li>
            );
        }

        if (item.type === 'group') {
            const accessibleSubModules = item.subModules.filter(subItem => hasAccess(subItem));

            if (accessibleSubModules.length === 0) return null;

            const isGroupActive = accessibleSubModules.some(subItem => {
                if (subItem.type === 'sub-group') return subItem.subModules.some(link => isLinkActive(link.path));
                return isLinkActive(subItem.path);
            });

            // Use original text for the group key to ensure consistency
            const groupKey = (item.originalText || item.text).replace(/\s/g, '');
            const isExpanded = forceExpanded || isGroupExpanded[groupKey] || isGroupActive;

            // For search results, we want to show the group header but the sub-modules are already filtered
            // So we should not re-filter them here
            const subModulesToRender = item.subModules.filter(subItem => {
                // If we're in search mode and this is a filtered item, all subItems are already filtered
                // So we just need to check access
                return hasAccess(subItem);
            });

            return (
                <li key={item.originalText || item.text} className={styles.navGroup}>
                    <div
                        className={`${styles.navLink} ${styles.navGroupHeader} ${isGroupActive ? styles.navItemActive : ''}`}
                        onClick={() => handleGroupClick(groupKey)}
                        title={!isSidebarOpen ? item.text : ''}
                    >
                        {icon && <span className={styles.navIcon}>{icon}</span>}
                        <span className={styles.navText}>{item.text}</span>
                        {isSidebarOpen && (
                            <span className={styles.navGroupIcon}>{isExpanded ? '▲' : '▼'}</span>
                        )}
                    </div>
                    {isSidebarOpen && (
                        <ul className={`${styles.navSubList} ${isExpanded ? styles.isExpanded : styles.isCollapsed}`}>
                            {subModulesToRender.map(subItem => (
                                <React.Fragment key={subItem.path || subItem.originalText || subItem.text}>
                                    {subItem.type === 'sub-group' ?
                                        renderSubGroupItem(subItem, groupKey, forceExpanded) :
                                        (
                                            <li key={subItem.path} className={styles.navSubItem}>
                                                {renderLinkContent(subItem.text, subItem.path, 2, null)}
                                            </li>
                                        )
                                    }
                                </React.Fragment>
                            ))}
                        </ul>
                    )}
                </li>
            );
        }
        return null;
    };

    // Get current role bracket for conditional rendering
    const roleBracket = getUserRoleBracket(userRole);
    const canSwitchSystem = roleBracket === 'DUAL_ACCESS';

    // System display name
    const getSystemDisplayName = () => {
        return activeSystem === SYSTEM_TYPES.BASE ? t('system.base') : t('system.luxury');
    };

    const systemDisplayName = getSystemDisplayName();

    // Loading state
    if (authLoading || permsLoading) {
        return (
            <div className={styles.loadingOverlay}>
                <div className={styles.spinnerContainer}>
                    <div className={styles.spinnerRing}></div>
                    <div className={styles.spinnerRing}></div>
                    <div className={styles.spinnerRing}></div>
                    <div className={styles.logoPulse}>{t('app.title')}</div>
                    <p className={styles.loadingMessage}>{t('app.loading')}</p>
                </div>
            </div>
        );
    }

    if (!currentUser) return <div style={{ padding: '30px', textAlign: 'center' }}>{t('app.accessDenied')}</div>;

    return (
        <div className={`${styles.layoutContainer} ${!isSidebarOpen ? styles.sidebarClosed : ''}`}>
            <nav className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                    <div
                        className={`${styles.sidebarTitle} ${canSwitchSystem ? styles.clickableLogo : ''}`}
                        onClick={canSwitchSystem ? () => setIsDropdownOpen(!isDropdownOpen) : undefined}
                        ref={canSwitchSystem ? dropdownRef : null}
                    >
                        <div className={styles.logoWrapper}>
                            <span className={styles.logoText}>
                                E-<span className={styles.logoHighlight}>MRP</span>
                            </span>
                            {canSwitchSystem && isSidebarOpen && (
                                <span className={styles.dropdownArrow}>▼</span>
                            )}
                        </div>
                        {isSidebarOpen && (
                            <span className={styles.systemBadge}>{systemDisplayName}</span>
                        )}

                        {/* System Switcher Dropdown for Dual-Access Users */}
                        {canSwitchSystem && isDropdownOpen && isSidebarOpen && (
                            <div className={styles.systemDropdown}>
                                <div className={styles.dropdownHeader}>
                                    <strong>{t('system.switch')}</strong>
                                </div>
                                <button
                                    className={`${styles.dropdownItem} ${activeSystem === SYSTEM_TYPES.BASE ? styles.active : ''}`}
                                    onClick={() => handleSystemSwitch(SYSTEM_TYPES.BASE)}
                                >
                                    <div>
                                        <div className={styles.dropdownTitle}>{t('system.base')}</div>
                                    </div>
                                </button>
                                <button
                                    className={`${styles.dropdownItem} ${activeSystem === SYSTEM_TYPES.LUXURY ? styles.active : ''}`}
                                    onClick={() => handleSystemSwitch(SYSTEM_TYPES.LUXURY)}
                                >
                                    <div>
                                        <div className={styles.dropdownTitle}>{t('system.luxury')}</div>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                    <button className={styles.toggleBtnInside} onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                        {isSidebarOpen ? '❮' : '❯'}
                    </button>
                </div>

                {/* ============================================
                    SEARCH BAR
                    ============================================ */}
                {isSidebarOpen && (
                    <div className={styles.searchContainer}>
                        <div className={styles.searchWrapper}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className={styles.searchInput}
                                placeholder={t('navigation.searchPlaceholder', 'Buscar módulos...')}
                                value={searchQuery}
                                onChange={(e) => {
                                    const query = e.target.value;
                                    setSearchQuery(query);
                                    // Expand all groups when searching
                                    if (query.trim()) {
                                        const allExpanded = {};
                                        filteredMenuItems.forEach(item => {
                                            if (item.type === 'group') {
                                                const groupKey = (item.originalText || item.text).replace(/\s/g, '');
                                                allExpanded[groupKey] = true;
                                            }
                                        });
                                        setIsGroupExpanded(allExpanded);
                                    } else {
                                        setIsGroupExpanded({});
                                    }
                                }}
                                aria-label={t('navigation.searchPlaceholder', 'Buscar módulos...')}
                            />
                            {searchQuery && (
                                <button
                                    className={styles.searchClear}
                                    onClick={() => {
                                        setSearchQuery('');
                                        setIsGroupExpanded({});
                                        searchInputRef.current?.focus();
                                    }}
                                    aria-label="Clear search"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className={styles.sidebarScrollArea}>
                    <ul className={styles.navList}>
                        {searchQuery.trim() && !hasSearchResults ? (
                            <li className={styles.noResults}>
                                <span className={styles.noResultsIcon}><FiSearch /></span>
                                <span className={styles.noResultsText}>
                                    {t('navigation.noResults', 'Nenhum módulo encontrado')}
                                </span>
                            </li>
                        ) : (
                            filteredMenuItems.map(item => (
                                <React.Fragment key={item.path || item.originalText || item.text}>
                                    {renderModuleItem(item, !!searchQuery.trim())}
                                </React.Fragment>
                            ))
                        )}
                    </ul>
                </div>
                {/* Logout Button at Bottom of Sidebar */}
                <div className={styles.sidebarFooter}>
                    <button onClick={handleLogout} className={styles.logoutButton} title={t('app.logout')}>
                        <FaSignOutAlt className={styles.logoutIcon} />
                        {isSidebarOpen && <span className={styles.logoutText}>{t('app.logout')}</span>}
                    </button>
                </div>
            </nav>
            <div className={styles.mainWrapper}>
                <header className={styles.header}>
                    <h1 className={styles.headerTitle}>
                        {t('app.welcome')}, <strong>{currentUser?.nome || currentUser?.email}</strong>
                    </h1>
                    <div className={styles.headerActions}>
                        {hasNotificationPermissions && (
                            <NotificationBell
                                onChatNotificationClick={handleChatNotificationClick}
                            />
                        )}
                        <button
                            className={styles.chatButton}
                            onClick={() => {
                                setSelectedChatChannelId(null);
                                setIsChatOpen(true);
                            }}
                            title={t('chat.title')}
                            aria-label={t('chat.title')}
                        >
                            <FaComments />
                            {isSidebarOpen && (
                                <span className={styles.chatBadge}>●</span>
                            )}
                        </button>
                        <button
                            className={styles.settingsButton}
                            onClick={() => setIsSettingsOpen(true)}
                            title={t('settings.title')}
                            aria-label={t('settings.title')}
                        >
                            <FaCog />
                        </button>
                    </div>
                </header>
                <main className={styles.mainContent}>{children}</main>
            </div>
            <SettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
            />
            {isChatOpen && (
                <div
                    className={styles.chatOverlay}
                    onClick={(e) => {
                        // Only close if clicking on the overlay, not the chat itself
                        if (e.target === e.currentTarget) {
                            handleCloseChat();
                        }
                    }}
                >
                    <div onClick={e => e.stopPropagation()}>
                        <Chat
                            isOpen={isChatOpen}
                            onClose={handleCloseChat}
                            initialChannelId={selectedChatChannelId}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layout;